import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { formatJobDate } from "../../services/jobsService";
import { triggerSyncJob } from "../../services/syncService";
import {
  fetchDownloadFiles,
  downloadAllFilesParallel,
  isFileSystemAccessSupported,
} from "../../services/s3DownloadService";
import { deleteJobFiles } from "../../services/deleteFilesService";
import { FlagIcon } from "../../constants/languageFlags";
import {
  GreenCheckCircleSmallIcon,
  RedCrossCircleSmallIcon,
  GreyPauseCircleSmallIcon,
  AmberSpinnerCircleSmallIcon,
  ChevronDownIcon,
  PlayIcon,
  DownloadIcon,
} from "../../icons";
import { LANG_STATUS_META, STATUS_META } from "../../constants";
import "./CollapsibleJobRow.scss";

const getLangStatusMeta = (status = "") =>
  LANG_STATUS_META[(status || "").toUpperCase()] || {
    label: status || "—",
    color: "#64748b",
    bg: "#f8fafc",
    border: "#cbd5e1",
  };

// Local aliases pointing to centralized icons
const CheckCircle = () => <GreenCheckCircleSmallIcon />;
const CrossCircle = () => <RedCrossCircleSmallIcon />;
const DashCircle = () => <GreyPauseCircleSmallIcon />;
const ProcessingCircle = () => <AmberSpinnerCircleSmallIcon className="cjr-spin" />;
const DeletedCircle = () => <GreenCheckCircleSmallIcon />;

const getAudioVideoIcons = (syncStatus = "") => {
  switch ((syncStatus || "").toUpperCase()) {
    case "VIDEO_TRANSLATED":
    case "READY":
    case "COMPLETED":
      return { audio: <CheckCircle />, video: <CheckCircle /> };

    case "AUDIO_TRANSLATED":
      return { audio: <CheckCircle />, video: <ProcessingCircle /> };

    case "FAILED":
      return { audio: <CrossCircle />, video: <CrossCircle /> };

    //    Show slate trash icon so user knows it finished but is now gone
    case "DELETED":
      return { audio: <DeletedCircle />, video: <DeletedCircle /> };

    case "PROCESSING":
    case "AV_SYNC_INITIATED":
      return { audio: <ProcessingCircle />, video: <ProcessingCircle /> };

    case "UPLOADED":
    case "PENDING":
    default:
      return { audio: <DashCircle />, video: <DashCircle /> };
  }
};

const ChevronIcon = ({ expanded }) => (
  <span className={`cjr-chevron-btn${expanded ? " cjr-chevron-btn--open" : ""}`}>
    <ChevronDownIcon size={14} expanded={expanded} />
  </span>
);

const getStatusMeta = (status = "") =>
  STATUS_META[(status || "").toUpperCase()] || {
    label: status,
    color: "#64748b",
    bg: "#f8fafc",
    dot: "#94a3b8",
  };

const gf = (obj, ...keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
  }
  return null;
};

// Used to promote AV_SYNC_INITIATED → Ready to Download without waiting for
// the backend to update processing_status to COMPLETED.
const allLangsVideoTranslated = (langStatuses = []) =>
  langStatuses.length > 0 &&
  langStatuses.every(
    (ls) =>
      (gf(ls, "sync_status", "SyncStatus", "status", "Status") || "").toUpperCase() ===
      "VIDEO_TRANSLATED"
  );

function ActionCell({ job, langStatuses, userEmail, onRefresh, onOptimisticStatus }) {
  // 'sync'     phase: idle | loading | error
  // 'download' phase: idle | loading | done | error
  const [syncState, setSyncState] = useState("idle"); // idle | loading | error
  const [syncError, setSyncError] = useState("");
  const [dlState, setDlState] = useState("idle"); // idle | loading | done | error
  const [dlError, setDlError] = useState("");

  const jobId = gf(job, "job_id", "jobId", "id", "Job_id", "JobId", "JOB_ID");

  const ps = (job.processing_status || "").toUpperCase();

  const handleStartSync = useCallback(
    async (e) => {
      e.stopPropagation();
      setSyncState("loading");
      setSyncError("");
      try {
        await triggerSyncJob(jobId, userEmail);
        // Optimistically switch the row to AV_SYNC_INITIATED so the UI
        // immediately shows the spinning indicator without waiting for
        // the next polling cycle.
        onOptimisticStatus?.(jobId, "AV_SYNC_INITIATED");
        setSyncState("idle");
        // Refresh job list after a short delay (backend needs time to persist)
        onRefresh?.();
      } catch (err) {
        console.error("[ActionCell] Sync trigger failed:", err.message);
        setSyncState("error");
        setSyncError(err.message || "Failed to start sync. Please try again.");
      }
    },
    [jobId, userEmail, onOptimisticStatus, onRefresh]
  );

  const handleDownload = useCallback(
    async (e) => {
      e.stopPropagation();

      // Ask for a target folder FIRST (needs the click gesture, so before any other await).
      // Unsupported browser / cancelled picker => dirHandle stays null => legacy download path.
      let dirHandle = null;
      if (isFileSystemAccessSupported()) {
        try {
          // startIn: "downloads" opens the picker inside the OS Downloads folder so the user
          // only has to click "Select" (avoids the "contains system files" error).
          dirHandle = await window.showDirectoryPicker({
            mode: "readwrite",
            startIn: "downloads",
          });
        } catch (pickErr) {
          console.warn(
            "[ActionCell] Folder picker skipped, using standard download:",
            pickErr?.message
          );
        }
      }

      setDlState("loading");
      setDlError("");
      try {
        // Guard: ensure required job fields are present before hitting the API
        if (!jobId) {
          throw new Error("Job ID is missing — cannot download. Please refresh and try again.");
        }
        if (!job.original_filename) {
          throw new Error("Filename is missing — cannot download. Please refresh and try again.");
        }

        // Step 1: Fetch presigned URLs from the download API
        const files = await fetchDownloadFiles(job.original_filename, jobId);

        // Step 2: Download all translated files in parallel
        const result = await downloadAllFilesParallel(files, () => {}, dirHandle);

        // Only clean up S3 when EVERY file was received/saved; otherwise keep the files
        // so the user can retry (previously delete also ran on partial failure).
        if (result.failed > 0) {
          throw new Error(
            `${result.failed} of ${result.failed + result.succeeded} file(s) failed to download. Please retry.`
          );
        }

        // Step 3: Call delete API to clean up S3 assets (non-blocking on error)
        deleteJobFiles(jobId).catch((err) =>
          console.warn("[ActionCell] delete_files failed (non-blocking):", err.message)
        );

        setDlState("done");
        onOptimisticStatus?.(jobId, "DELETED");
        // Refresh the list so the server-side status is reflected
        onRefresh?.();
      } catch (err) {
        console.error("[ActionCell] Download failed:", err.message);
        setDlState("error");
        setDlError(err.message || "Download failed. Please retry.");
      }
    },
    [jobId, job.original_filename, onOptimisticStatus, onRefresh]
  );

  // Priority: local in-flight state wins over server status so the UI
  // always reflects the latest user action without a full page refresh.

  if (dlState === "loading") {
    return (
      <span className="cjr-action-busy">
        <span className="cjr-action-spinner" /> Downloading…
      </span>
    );
  }

  // DELETED is the only true terminal state — hide the Download button.
  if (ps === "DELETED") {
    return <span className="cjr-action-badge cjr-action-badge--done">✓ Downloaded</span>;
  }

  if (dlState === "done") {
    return <span className="cjr-action-badge cjr-action-badge--done">✓ Downloaded</span>;
  }

  if (dlState === "error") {
    return (
      <div className="cjr-action-error-wrap">
        <button
          className="cjr-action-btn cjr-action-btn--download"
          onClick={handleDownload}
          title={dlError}
        >
          ↺ Retry Download
        </button>
        <span className="cjr-action-error-msg" title={dlError}>
          {dlError.length > 40 ? dlError.slice(0, 37) + "…" : dlError}
        </span>
      </div>
    );
  }

  if (syncState === "loading") {
    return (
      <span className="cjr-action-busy cjr-action-busy--sync">
        <span className="cjr-action-spinner" /> Starting…
      </span>
    );
  }

  if (syncState === "error") {
    return (
      <div className="cjr-action-error-wrap">
        <button
          className="cjr-action-btn cjr-action-btn--sync"
          onClick={handleStartSync}
          title={syncError}
        >
          ↺ Retry Sync
        </button>
        <span className="cjr-action-error-msg" title={syncError}>
          {syncError.length > 40 ? syncError.slice(0, 37) + "…" : syncError}
        </span>
      </div>
    );
  }

  if (ps === "UPLOADED") {
    return (
      <button className="cjr-action-btn cjr-action-btn--sync" onClick={handleStartSync}>
        <PlayIcon size={13} />
        Start Sync
      </button>
    );
  }

  // If every language already reports VIDEO_TRANSLATED the job is effectively
  // complete — show Download immediately without waiting for the backend to
  // flip processing_status to COMPLETED.
  if (ps === "AV_SYNC_INITIATED") {
    if (allLangsVideoTranslated(langStatuses)) {
      return (
        <button className="cjr-action-btn cjr-action-btn--download" onClick={handleDownload}>
          <DownloadIcon size={13} />
          Download
        </button>
      );
    }
    return (
      <span className="cjr-action-busy cjr-action-busy--sync">
        <span className="cjr-action-spinner" /> Syncing…
      </span>
    );
  }

  // DOWNLOADED means BE sent presigned URLs but files may not be downloaded yet.
  // Keep button visible until status = DELETED (files truly gone from S3).
  if (ps === "COMPLETED" || ps === "DOWNLOADED") {
    return (
      <button className="cjr-action-btn cjr-action-btn--download" onClick={handleDownload}>
        <DownloadIcon size={13} />
        Download
      </button>
    );
  }

  if (ps === "FAILED") {
    return <span className="cjr-action-badge cjr-action-badge--failed">⚠ Failed</span>;
  }

  return <span className="cjr-action-none">—</span>;
}

function CollapsibleJobRow({
  job,
  isEven,
  isExpanded,
  onToggle,
  userEmail,
  onRefresh,
  onOptimisticStatus,
}) {
  const statusMeta = getStatusMeta(job.processing_status);

  // language_statuses array from API (may be undefined / empty)
  const langStatuses = Array.isArray(job.language_statuses) ? job.language_statuses : [];

  return (
    <>
      {/* ── Main clickable row ─────────────────────────────────────────── */}
      <tr
        className={`jd-tr${isEven ? " jd-tr--even" : ""} cjr-main-row${isExpanded ? " cjr-main-row--expanded" : ""}`}
        onClick={onToggle}
        style={{ cursor: "pointer" }}
        aria-expanded={isExpanded}
      >
        {/* File Name */}
        <td className="jd-td jd-td--filename">
          <div className="jd-filename-wrap">
            <span className="jd-file-icon">🎬</span>
            <span className="jd-filename" title={job.original_filename}>
              {job.original_filename || "—"}
            </span>
          </div>
        </td>

        {/* Status */}
        <td className="jd-td">
          {(() => {
            // Override badge to "Ready to Download" when all langs are VIDEO_TRANSLATED
            const isReadyToDownload =
              (job.processing_status || "").toUpperCase() === "AV_SYNC_INITIATED" &&
              allLangsVideoTranslated(langStatuses);
            const badgeMeta = isReadyToDownload
              ? {
                  label: "Ready to Download",
                  color: "#0d9488",
                  bg: "#f0fdfa",
                  dot: "#0d9488",
                }
              : statusMeta;
            return (
              <span
                className="jd-status-badge"
                style={{
                  color: badgeMeta.color,
                  background: badgeMeta.bg,
                  borderColor: badgeMeta.color + "33",
                }}
              >
                <span className="jd-status-dot" style={{ background: badgeMeta.dot }} />
                {badgeMeta.label}
              </span>
            );
          })()}
        </td>

        {/* Service */}
        <td className="jd-td">
          <span className="jd-service-badge">
            {job.service_type
              ? job.service_type.charAt(0).toUpperCase() + job.service_type.slice(1).toLowerCase()
              : "—"}
          </span>
        </td>

        {/* Created */}
        <td className="jd-td jd-td--updated">
          <span className="jd-updated">{formatJobDate(job.created_at)}</span>
        </td>

        {/* Updated */}
        <td className="jd-td jd-td--updated">
          <span className="jd-updated">{formatJobDate(job.updated_at)}</span>
        </td>

        {/* Action — buttons inside already call e.stopPropagation();
             clicking the cell background still toggles the row */}
        <td className="jd-td cjr-action-cell">
          <ActionCell
            job={job}
            langStatuses={langStatuses}
            userEmail={userEmail}
            onRefresh={onRefresh}
            onOptimisticStatus={onOptimisticStatus}
          />
        </td>

        {/* Expand chevron */}
        <td className="jd-td cjr-chevron-cell">
          <ChevronIcon expanded={isExpanded} />
        </td>
      </tr>

      {/* ── Expanded language-status panel ────────────────────────────── */}
      {isExpanded && (
        <tr className="cjr-expand-row">
          <td colSpan={7} className="cjr-expand-cell">
            <div className="cjr-panel">
              <div className="cjr-panel-header">
                <span className="cjr-panel-title">Language Status</span>
                <span className="cjr-panel-count">
                  {langStatuses.length} language
                  {langStatuses.length !== 1 ? "s" : ""}
                </span>
              </div>

              {langStatuses.length === 0 ? (
                <div className="cjr-no-langs">No language data available.</div>
              ) : (
                <div className="cjr-lang-table-wrap">
                  <table className="cjr-lang-table">
                    <thead>
                      <tr>
                        <th className="cjr-lth">Language</th>
                        <th className="cjr-lth">Voice</th>
                        <th className="cjr-lth">Engine</th>
                        <th className="cjr-lth">Gender</th>
                        <th className="cjr-lth cjr-lth--center">Audio</th>
                        <th className="cjr-lth cjr-lth--center">Video</th>
                        <th className="cjr-lth">Status</th>
                        <th className="cjr-lth">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {langStatuses.map((ls, i) => {
                        const rawStatus =
                          gf(ls, "sync_status", "SyncStatus", "status", "Status") || "";
                        const lsMeta = getLangStatusMeta(rawStatus);
                        const avIcons = getAudioVideoIcons(rawStatus);
                        return (
                          <tr key={i} className="cjr-ltr">
                            {/* Language + flag */}
                            <td className="cjr-ltd">
                              <div className="cjr-lang-name-wrap">
                                <span className="cjr-flag">
                                  <FlagIcon
                                    language={gf(ls, "language", "Language") || ""}
                                    size={20}
                                  />
                                </span>
                                <span className="cjr-lang-name">
                                  {gf(ls, "language", "Language") || "—"}
                                </span>
                              </div>
                            </td>

                            {/* Voice */}
                            <td className="cjr-ltd">
                              {gf(
                                ls,
                                "audio_voice_id",
                                "voice_name",
                                "Voice",
                                "VoiceName",
                                "voice_id",
                                "VoiceId",
                                "voiceName"
                              ) || "—"}
                            </td>

                            {/* Engine */}
                            <td className="cjr-ltd">
                              {gf(
                                ls,
                                "audio_voice_engine",
                                "engine",
                                "Engine",
                                "engineType",
                                "EngineType"
                              ) || "—"}
                            </td>

                            {/* Gender */}
                            <td className="cjr-ltd cjr-ltd--gender">
                              {(() => {
                                const g = gf(ls, "audio_voice_gender", "gender", "Gender");
                                return g
                                  ? g.charAt(0).toUpperCase() + g.slice(1).toLowerCase()
                                  : "—";
                              })()}
                            </td>

                            {/* Audio — icon driven by sync_status */}
                            <td className="cjr-ltd cjr-ltd--center">{avIcons.audio}</td>

                            {/* Video — icon driven by sync_status */}
                            <td className="cjr-ltd cjr-ltd--center">{avIcons.video}</td>

                            {/* Status badge */}
                            <td className="cjr-ltd">
                              <span
                                className="cjr-lang-status-badge"
                                style={{
                                  color: lsMeta.color,
                                  background: lsMeta.bg,
                                  borderColor: lsMeta.border,
                                }}
                              >
                                {lsMeta.label}
                              </span>
                            </td>

                            {/* Last updated */}
                            <td className="cjr-ltd cjr-ltd--date">
                              {formatJobDate(
                                gf(
                                  ls,
                                  "updated_at",
                                  "UpdatedAt",
                                  "last_updated",
                                  "LastUpdated",
                                  "timestamp",
                                  "Timestamp"
                                )
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

ActionCell.propTypes = {
  job: PropTypes.shape({
    processing_status: PropTypes.string,
    original_filename: PropTypes.string,
  }),
  langStatuses: PropTypes.array,
  userEmail: PropTypes.string,
  onRefresh: PropTypes.func,
  onOptimisticStatus: PropTypes.func,
};

ChevronIcon.propTypes = {
  expanded: PropTypes.bool,
};

CollapsibleJobRow.propTypes = {
  job: PropTypes.shape({
    processing_status: PropTypes.string,
    original_filename: PropTypes.string,
    service_type: PropTypes.string,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    language_statuses: PropTypes.array,
  }),
  isEven: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  userEmail: PropTypes.string,
  onRefresh: PropTypes.func,
  onOptimisticStatus: PropTypes.func,
};

export default CollapsibleJobRow;
